import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState } from "react";
// framer-motion removed from the critical path — the home page uses
// tiny local stand-ins that drop the animation props but keep layout/style
// identical. This shaves ~40 KB gzip off the initial JS bundle and
// eliminates the framer-motion hydration work that was inflating TBT.
const M = ({ initial: _i, whileInView: _w, viewport: _v, transition: _t, ...rest }: any) => <div {...rest} />;
const Mli = ({ initial: _i, whileInView: _w, viewport: _v, transition: _t, ...rest }: any) => <li {...rest} />;
import { ArrowRight, Upload, ShieldCheck, Zap, HeadphonesIcon, Sparkles, CheckCircle2, FileText, Star, Activity, Clock, LayoutGrid, BadgeCheck, Briefcase, Plane, Receipt, Calculator, Landmark, BadgePercent, Banknote, CreditCard, Laptop, Coins, Lock, MessageSquare } from "lucide-react";
import { getIcon, hasIcon } from "@/lib/icon-registry";
import { SiteShell } from "@/components/site/SiteShell";
import { ServiceDetailSection } from "@/components/site/ServiceDetailSection";
import { SERVICE_DETAIL_CONTENT } from "@/lib/service-detail-content";
// AuthBackdrop is purely decorative (-z-10, opacity 0.06–0.10). Ship it out
// of the critical desktop bundle — it hydrates on idle after first paint
// via its own internal requestIdleCallback gate.
const AuthBackdrop = lazy(() =>
  import("@/components/site/AuthBackdrop").then((m) => ({ default: m.AuthBackdrop })),
);
import { SectionHeading } from "@/components/site/SectionHeading";
import { DeferUntilIdle } from "@/components/site/DeferUntilIdle";
import { MobileLazyOnVisible } from "@/components/site/MobileLazyOnVisible";

import { Button } from "@/components/ui/button";
import { useIsB2B } from "@/hooks/use-b2b";
import { Crown } from "lucide-react";
import { useTranslation } from "react-i18next";
import ctaBg from "@/assets/cta-tax-bg.jpg?w=640;1024;1600;1920&format=avif;webp;jpg&as=picture";
import { jsonLdScript } from "@/lib/jsonld";

// Defer below-the-fold widgets so they don't block initial paint on mobile.
const TaxCalculatorWidget = lazy(() =>
  import("@/components/site/TaxCalculatorWidget").then((m) => ({ default: m.TaxCalculatorWidget })),
);
const PaymentLinksWidget = lazy(() =>
  import("@/components/site/PaymentLinksWidget").then((m) => ({ default: m.PaymentLinksWidget })),
);
const VisaTaxFloatingButton = lazy(() =>
  import("@/components/site/VisaTaxWidget").then((m) => ({ default: m.VisaTaxFloatingButton })),
);
const PopupOffer = lazy(() =>
  import("@/components/site/PopupOffer").then((m) => ({ default: m.PopupOffer })),
);
const FloatingPromoCard = lazy(() =>
  import("@/components/site/FloatingPromoCard").then((m) => ({ default: m.FloatingPromoCard })),
);
const TestimonialsSection = lazy(() =>
  import("@/components/site/TestimonialsSection").then((m) => ({ default: m.TestimonialsSection })),
);
const CampaignSection = lazy(() =>
  import("@/components/site/CampaignSection").then((m) => ({ default: m.CampaignSection })),
);

import { getCampaignSettings } from "@/lib/campaign.functions";

export const Route = createFileRoute("/")({
  // NOTE: Previously this loader ran `getCampaignSettings()` on every SSR
  // request, adding a Supabase round-trip to TTFB even when no campaign was
  // active. The CampaignSection is lazy-mounted below the hero anyway, so
  // we defer the fetch to a client-side idle call inside CampaignDeadlineSlot.
  // This eliminates one network dependency from the critical path.
  head: () => ({
    meta: [
      { title: "Online Tax VAT | Vat Online BD | E Return & Income Tax Services in Bangladesh" },
      { name: "description", content: "Vat Online Bangladesh — file your e tax return, e-TIN registration and VAT return online. Trusted NBR e return submission and income tax & VAT services in Bangladesh." },
      { name: "keywords", content: "vat online, e return, e return bd, e tax return, e return nbr, income tax return, e tin, e-tin registration online, online vat verification, vat registration bangladesh, tax return filing" },
      { property: "og:title", content: "Online Tax VAT | Vat Online BD | E Return & Income Tax Services in Bangladesh" },
      { property: "og:description", content: "Vat Online Bangladesh — file your e tax return, e-TIN registration and VAT return online. Trusted NBR e return submission and income tax & VAT services in Bangladesh." },
      { property: "og:url", content: "https://onlinetaxvat.com/" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Online Tax VAT | Vat Online BD | E Return & Income Tax Services in Bangladesh" },
      { name: "twitter:description", content: "Vat Online Bangladesh — file your e tax return, e-TIN registration and VAT return online. Trusted NBR e return submission and income tax & VAT services in Bangladesh." },
    ],
    links: [{ rel: "canonical", href: "https://onlinetaxvat.com/" }],
    scripts: [
      jsonLdScript({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": "https://onlinetaxvat.com/#webpage",
          url: "https://onlinetaxvat.com/",
          name: "VAT Online Bangladesh — Pay VAT Online, VAT Registration & Return",
          isPartOf: { "@id": "https://onlinetaxvat.com/#website" },
          about: { "@id": "https://onlinetaxvat.com/#organization" },
          inLanguage: "en",
          description: "Pay VAT online in Bangladesh, VAT Registration (BIN), VAT Return (Mushak 9.1), TIN & Income Tax filing online.",
      }),
      jsonLdScript({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "VAT & Tax Services Online in Bangladesh",
          itemListElement: [
            { "@type": "ListItem", position: 1, item: { "@type": "Service", name: "Pay VAT Online Bangladesh", serviceType: "VAT Payment", areaServed: "BD", provider: { "@id": "https://onlinetaxvat.com/#organization" } } },
            { "@type": "ListItem", position: 2, item: { "@type": "Service", name: "Online VAT Registration (BIN)", serviceType: "VAT Registration", areaServed: "BD", provider: { "@id": "https://onlinetaxvat.com/#organization" } } },
            { "@type": "ListItem", position: 3, item: { "@type": "Service", name: "Online VAT Return (Mushak 9.1)", serviceType: "VAT Return Filing", areaServed: "BD", provider: { "@id": "https://onlinetaxvat.com/#organization" } } },
            { "@type": "ListItem", position: 4, item: { "@type": "Service", name: "Income Tax Return Filing Online", serviceType: "Income Tax Return", areaServed: "BD", provider: { "@id": "https://onlinetaxvat.com/#organization" } } },
            { "@type": "ListItem", position: 5, item: { "@type": "Service", name: "TIN Certificate Online", serviceType: "TIN Registration", areaServed: "BD", provider: { "@id": "https://onlinetaxvat.com/#organization" } } },
            { "@type": "ListItem", position: 6, item: { "@type": "Service", name: "VAT Compliance & Audit", serviceType: "VAT Audit", areaServed: "BD", provider: { "@id": "https://onlinetaxvat.com/#organization" } } },
          ],
      }),
      jsonLdScript({
          "@context": "https://schema.org",
          "@type": "Service",
          "@id": "https://onlinetaxvat.com/#service-online-tax-vat",
          name: "Online Tax & VAT Filing Services in Bangladesh",
          serviceType: "Tax & VAT Consultancy",
          description: "End-to-end online tax return filing, e-Return submission, e-TIN registration, VAT registration (BIN) and VAT return (Mushak 9.1) for individuals and businesses in Bangladesh.",
          provider: { "@id": "https://onlinetaxvat.com/#organization" },
          areaServed: { "@type": "Country", name: "Bangladesh" },
          availableLanguage: ["en", "bn"],
          url: "https://onlinetaxvat.com/",
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Online Tax & VAT Services",
            itemListElement: [
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Income Tax Return Filing Online" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "E-Return (NBR e-Return) Submission" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "E-TIN Registration Online" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "VAT Registration (BIN)" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "VAT Return (Mushak 9.1) Filing" } },
            ],
          },
      }),
      jsonLdScript({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": "https://onlinetaxvat.com/#faq",
          mainEntity: [
            {
              "@type": "Question",
              name: "How do I file my income tax return online in Bangladesh?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "You can file your income tax return online through the NBR e-Return portal. Our consultants prepare your computation, apply eligible rebates and submit the return on your behalf, then share the official acknowledgement slip and computation sheet.",
              },
            },
            {
              "@type": "Question",
              name: "What is E Return BD and who needs to submit it?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "E Return BD refers to the NBR e-Return system for electronic income tax return filing in Bangladesh. Salaried individuals, freelancers, business owners and companies with an active TIN are required to submit their annual e tax return.",
              },
            },
            {
              "@type": "Question",
              name: "How do I get an e-TIN certificate online?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "An e-TIN certificate is issued through the NBR e-TIN portal using your NID, mobile number and email. We handle new e-TIN registration, recovery of lost TIN and correction requests end-to-end.",
              },
            },
            {
              "@type": "Question",
              name: "How does online VAT registration (BIN) work in Bangladesh?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Online VAT registration is done via the NBR VAT Online Project (vat.gov.bd) by submitting Mushak 2.1 along with your trade license, TIN and business documents. The 13-digit BIN certificate is typically issued within 3–5 working days.",
              },
            },
          ],
      }),
    ],
  }),
  component: HomePage,
});


const trustBadges = [
  { icon: ShieldCheck, labelKey: "home.trust.secure", subKey: "home.trust.secureSub" },
  { icon: Zap, labelKey: "home.trust.fast", subKey: "home.trust.fastSub" },
  { icon: HeadphonesIcon, labelKey: "home.trust.expert", subKey: "home.trust.expertSub" },
];

const serviceBn: Record<string, string> = {
  "income-tax-return": "ইনকাম ট্যাক্স রিটার্ন",
  "vat-registration": "ভ্যাট রেজিস্ট্রেশন",
  "bin-registration": "BIN রেজিস্ট্রেশন",
  "tin-certificate": "TIN সার্টিফিকেট",
  "etin-verification": "e-TIN ভেরিফিকেশন",
  "company-tax-filing": "কোম্পানি ট্যাক্স ফাইলিং",
  "trade-license": "ট্রেড লাইসেন্স সাপোর্ট",
  "tax-calculation": "ট্যাক্স ক্যালকুলেশন",
  "vat-return": "ভ্যাট রিটার্ন সাবমিশন",
  "tax-consultation": "ট্যাক্স কনসালটেশন",
  "document-review": "ডকুমেন্ট রিভিউ",
  "live-chat": "লাইভ চ্যাট সাপোর্ট",
};

import { ListChecks, CloudUpload, PackageCheck, Wallet } from "lucide-react";

const howStepKeys = [
  { step: "01", titleKey: "home.howSection.step1Title", descKey: "home.howSection.step1Desc", Icon: ListChecks, gradient: "from-emerald-500 to-teal-500", accent: "from-emerald-500/15 to-teal-500/10", ring: "ring-emerald-500/30" },
  { step: "02", titleKey: "home.howSection.step2Title", descKey: "home.howSection.step2Desc", Icon: CloudUpload, gradient: "from-sky-500 to-blue-600", accent: "from-sky-500/15 to-blue-500/10", ring: "ring-sky-500/30" },
  { step: "03", titleKey: "home.howSection.step3Title", descKey: "home.howSection.step3Desc", Icon: PackageCheck, gradient: "from-fuchsia-500 to-purple-600", accent: "from-fuchsia-500/15 to-purple-500/10", ring: "ring-fuchsia-500/30" },
  { step: "04", titleKey: "home.howSection.step4Title", descKey: "home.howSection.step4Desc", Icon: Wallet, gradient: "from-amber-500 to-orange-500", accent: "from-amber-500/15 to-orange-500/10", ring: "ring-amber-500/30" },
];

const fallbackHomePackages = [
  { name: "Basic", tagline: "Essential tax filing for individuals", price: "৳ 1,500", delivery: "5–7 business days", features: ["Single service handling", "Email support", "5–7 business days delivery", "Basic document review"], popular: false, priceType: "amount" as const },
  { name: "Standard", tagline: "Most popular for SMEs & professionals", price: "৳ 3,500", delivery: "3–4 business days", features: ["Up to 3 services", "WhatsApp + Email support", "3–4 business days delivery", "Document verification"], popular: true, priceType: "amount" as const },
  { name: "Premium", tagline: "Complete coverage for growing companies", price: "৳ 7,500", delivery: "1–2 business days", features: ["Unlimited service requests", "Dedicated tax advisor", "1–2 business days delivery", "Full audit support"], popular: false, priceType: "amount" as const },
  { name: "Custom Consultation", tagline: "Tailored solutions for enterprises", price: "Quote", delivery: "As per agreement", features: ["Custom scope of work", "Multi-entity coverage", "On-site visits available", "Dedicated team"], popular: false, priceType: "quote" as const },
];

function Icon({ name, className }: { name?: string | null; className?: string }) {
  const C = getIcon(name);
  return <C className={className} />;
}

// Dynamic Lucide icon resolver for service cards.
// 1) Explicit title overrides 2) keyword-based fallback 3) generic default.
function resolveServiceIcon(s: { title?: string | null; title_bn?: string | null; icon?: string | null }): string {
  const exact: Record<string, string> = {
    "আয়কর রিটার্ন": "FileText",
    "ভ্যাট রেজিস্ট্রেশন": "FileCheck",
    "ফাস্ট-ট্র্যাক ভ্যাট/বিন রেজিস্ট্রেশন": "Zap",
    "ফাস্ট-ট্র্যাক ভ্যাট / বিন রেজিস্ট্রেশন": "Zap",
    "ই-টিন রেজিস্ট্রেশন এবং রিকভারি": "IdCard",
    "ভ্যাট রিটার্ন সাবমিশন": "Send",
    "ট্যাক্স কনসালটেন্সি": "MessagesSquare",
    "ট্রেড লাইসেন্স সহায়তা": "ShieldCheck",
    "আইআরসি / ইআরসি রেজিস্ট্রেশন": "Globe",
    "আইআরসি/ইআরসি রেজিস্ট্রেশন": "Globe",
    "কোম্পানি ট্যাক্স রিটার্ন দাখিল": "Building",
  };
  const bn = (s.title_bn ?? "").trim();
  const en = (s.title ?? "").trim();
  if (bn && exact[bn]) return exact[bn];
  if (en && exact[en]) return exact[en];

  const hay = `${bn} ${en}`.toLowerCase();
  const has = (...keys: string[]) => keys.some((k) => hay.includes(k.toLowerCase()));

  if (has("ফাস্ট", "fast-track", "fast track", "fasttrack")) return "Zap";
  if (has("কনসালটেন্সি", "কনসালটেশন", "consult")) return "MessagesSquare";
  if (has("আইআরসি", "ইআরসি", "irc", "erc", "import", "export")) return "Globe";
  if (has("লাইসেন্স", "license", "licence")) return "ShieldCheck";
  if (has("কোম্পানি", "company", "corporate")) return "Building";
  if (has("ই-টিন", "etin", "e-tin", "রিকভারি", "recovery")) return "IdCard";
  if (has("ভ্যাট রিটার্ন", "vat return")) return "Send";
  if (has("ভ্যাট রেজিস্ট্রেশন", "vat registration", "বিন রেজিস্ট্রেশন", "bin registration")) return "FileCheck";
  if (has("রিটার্ন", "return")) return "FileText";
  if (has("রেজিস্ট্রেশন", "registration")) return "FileCheck";
  if (has("টিন", "tin")) return "IdCard";

  // Honor any explicitly stored icon as a last hint before generic default.
  if (s.icon && hasIcon(s.icon)) return s.icon;
  return "Briefcase";
}

function CampaignDeadlineSlot() {
  const [campaign, setCampaign] = useState<any>(null);
  useEffect(() => {
    const w = window as any;
    const ric = (cb: () => void) =>
      typeof w.requestIdleCallback === "function"
        ? w.requestIdleCallback(cb, { timeout: 3000 })
        : window.setTimeout(cb, 500);
    const h = ric(() => {
      void getCampaignSettings({} as any)
        .then((c: any) => setCampaign(c))
        .catch(() => {});
    });
    return () => {
      if (typeof w.cancelIdleCallback === "function") w.cancelIdleCallback(h);
    };
  }, []);
  if (!campaign || !campaign.enabled) {
    return null;
  }
  return (
    <div className="campaign-deadline-slot campaign-deadline-slot--active" style={{ minHeight: 220, contentVisibility: "auto", containIntrinsicSize: "1px 220px" }}>
      <Suspense fallback={<div className="campaign-deadline-fallback" style={{ minHeight: 220 }} aria-hidden="true" />}>
        <CampaignSection initialData={campaign} />
      </Suspense>
    </div>
  );
}


function HomePage() {

  const [hero, setHero] = useState<any>(null);
  const [services, setServices] = useState<any[]>([]);
  const [testimonials, setTestimonials] = useState<any[]>([]);
  const [homePackages, setHomePackages] = useState<any[]>(fallbackHomePackages);
  const [siteSettings, setSiteSettings] = useState<any>(null);
  const { isB2B } = useIsB2B();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // Defer all non-critical data-fetching past first paint. Each fetch runs
    // in its own idle slot so the browser can yield to input/paint between
    // requests — prevents a single long task during hydration (drops TBT).
    const w = window as any;
    const ric = (cb: () => void, timeout = 1500) =>
      typeof w.requestIdleCallback === "function"
        ? w.requestIdleCallback(cb, { timeout })
        : window.setTimeout(cb, 200);

    const handles: number[] = [];
    // Import @/lib/content ONCE, then schedule each fetch in its own idle
    // slot so the browser can still yield between requests. Previously each
    // fetch redundantly re-invoked the dynamic import, re-parsing the same
    // module and re-registering promise microtasks on the main thread.
    const contentPromise = import("@/lib/content");
    handles.push(ric(() => { void contentPromise.then((m) => m.fetchHero().then(setHero)); }, 800));
    handles.push(ric(() => { void contentPromise.then((m) => m.fetchServices().then(setServices)); }, 1200));
    handles.push(ric(() => { void contentPromise.then((m) => m.fetchTestimonials().then(setTestimonials)); }, 2000));
    handles.push(ric(() => { void contentPromise.then((m) => m.fetchSiteSettings().then(setSiteSettings)); }, 1400));
    handles.push(
      ric(() => {
        void import("@/integrations/supabase/client").then(({ supabase }) => {
          (supabase.from as any)("home_packages")
            .select("*")
            .eq("active", true)
            .order("display_order", { ascending: true })
            .then(({ data }: any) => {
              if (data && data.length > 0) {
                setHomePackages(data.map((r: any) => ({
                  name: (i18n.language === "bn" && r.name_bn) ? r.name_bn : r.name,
                  tagline: (i18n.language === "bn" && r.tagline_bn) ? r.tagline_bn : r.tagline,
                  price: r.price,
                  delivery: (i18n.language === "bn" && r.delivery_bn) ? r.delivery_bn : r.delivery,
                  features: ((i18n.language === "bn" && Array.isArray(r.features_bn) && r.features_bn.length > 0) ? r.features_bn : r.features) ?? [],
                  popular: !!r.popular,
                  priceType: r.price_type ?? "amount",
                })));
              }
            });
        });
      }, 2500),
    );
    return () => {
      if (typeof w.cancelIdleCallback === "function") {
        handles.forEach((h) => w.cancelIdleCallback(h));
      }
    };
  }, [i18n.language]);

  return (
    <SiteShell>
      {/* HERO */}
      <section className="home-hero relative pt-12 md:pt-20 pb-20 md:pb-28 overflow-hidden">
        <Suspense fallback={null}><AuthBackdrop contained /></Suspense>
        <div className="container-page">
          <div className="home-hero-inner max-w-4xl mx-auto text-center">
            {isB2B && (
              <div className="mb-5 flex justify-center animate-[fade-in_0.3s_ease-out_both]">
                <div className="group relative inline-flex items-center gap-2.5 rounded-full px-8 py-3 shadow-[0_10px_30px_-10px_rgba(202,138,4,0.6)] ring-1 ring-amber-300/60 bg-[linear-gradient(135deg,#f5d76e_0%,#f0c419_25%,#fff3a0_50%,#caa64a_75%,#8b6914_100%)] bg-[length:200%_200%] animate-[shimmer_3s_ease-in-out_infinite] transition-transform duration-300 hover:scale-105 hover:shadow-[0_14px_36px_-10px_rgba(202,138,4,0.8)]">
                  <Crown className="h-5 w-5 text-amber-950 drop-shadow-sm" />
                  <span className="font-bn font-bold text-lg text-amber-950 tracking-wide drop-shadow-sm">{t("home.b2bBadge")}</span>
                  <span className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                </div>
              </div>
            )}
            {/* Reserve space to avoid CLS while hero data hydrates */}
            <div className="home-badge-slot min-h-[80px] md:min-h-[92px] flex items-center justify-center">
              {hero?.badge_text && (
                <div
                  className="inline-flex items-start gap-2 rounded-2xl glass px-5 py-3 text-base md:text-lg font-bn font-semibold shadow-soft text-center animate-[fade-in_0.3s_ease-out_both]">
                  <Sparkles className="h-5 w-5 text-primary mt-1 shrink-0" />
                  <span className="whitespace-pre-line leading-tight">{hero.badge_text.replace(/\s+(ট্রাস্টেড|Trusted)/, "\n$1")}</span>
                </div>
              )}
            </div>
            <h1
              className="home-hero-title mt-6 font-display font-bold tracking-tight text-4xl md:text-6xl lg:text-7xl leading-[1.05] font-bn">
              <span className="!text-emerald-600 dark:!text-white">{t("home.heroTitle")}</span>
            </h1>
            <p
              className="home-hero-subtitle mx-auto mt-4 max-w-4xl font-display font-semibold tracking-tight text-base sm:text-lg md:text-xl lg:text-2xl leading-snug text-black dark:text-white md:whitespace-nowrap font-bn"
            >
              {(i18n.resolvedLanguage || i18n.language || "en").startsWith("bn")
                ? "বাংলাদেশে এনবিআর ট্যাক্স এবং ভ্যাট কমপ্লায়েন্সে আপনার বিশ্বস্ত পার্টনার"
                : "Your Trusted Partner for NBR Tax & VAT Compliance in Bangladesh"}
            </p>
            {(() => {
              const isBn = (i18n.resolvedLanguage || i18n.language || "en").startsWith("bn");
              const label = isBn
                ? "বাংলাদেশে আয়কর রিটার্ন দাখিল: একটি পূর্ণাঙ্গ নির্দেশিকা"
                : "Income Tax Return Filing in Bangladesh: A Complete Guide";
              return (
                <div className="home-guide-link-wrap mt-4 flex justify-center animate-[fade-in_0.35s_ease-out_both]">
                  <Link
                    to="/knowledge-hub"
                    title={label}
                    className="home-guide-link inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-900/70 px-4 py-1.5 text-xs sm:text-sm font-semibold !text-black dark:!text-white shadow-sm backdrop-blur-sm hover:bg-emerald-900/80 hover:border-emerald-500/60 transition-all"
                  >
                    <FileText className="h-3.5 w-3.5 shrink-0 !text-black dark:!text-white" aria-hidden="true" />
                    <span className={`!text-black dark:!text-white ${isBn ? "font-bn" : ""}`}>{label}</span>
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 !text-black dark:!text-white" aria-hidden="true" />
                  </Link>
                </div>
              );
            })()}
            {/* Reserve space for the ticker banner to avoid CLS while hero data hydrates */}
            <div className="home-subtitle-slot mt-6 min-h-[72px] md:min-h-[88px] lg:min-h-[100px]">
              {hero?.subtitle && (
                <div
                  className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen overflow-hidden animate-[fade-in_0.4s_ease-out_both]">
                  {/* Premium gradient background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-700 via-teal-600 to-emerald-700 dark:from-emerald-900 dark:via-teal-800 dark:to-emerald-900" />
                  {/* Diagonal pattern overlay */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 opacity-10 dark:opacity-15"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(255,255,255,0.12) 8px, rgba(255,255,255,0.12) 16px)",
                    }}
                  />
                  {/* Soft glow orbs */}
                  <div aria-hidden="true" className="absolute -top-10 left-1/4 h-32 w-32 rounded-full bg-yellow-300/25 blur-3xl" />
                  <div aria-hidden="true" className="absolute -bottom-10 right-1/4 h-32 w-32 rounded-full bg-cyan-300/20 blur-3xl" />
                  {/* Top & bottom gold lines */}
                  <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-yellow-300/70 to-transparent" />
                  <div aria-hidden="true" className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-yellow-300/70 to-transparent" />

                  <div className="relative flex overflow-x-hidden py-3">
                    <div className="flex whitespace-nowrap animate-[ticker_30s_linear_infinite]">
                      {[0, 1, 2].map((i) => (
                        <span
                          key={i}
                          className="mx-8 text-xl md:text-2xl lg:text-3xl font-bn font-bold text-white dark:text-yellow-50 drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)] flex items-center gap-2"
                        >
                          <Sparkles className="h-5 w-5 text-yellow-300 shrink-0" />
                          {hero.subtitle}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="home-hero-actions mt-9 flex flex-wrap items-center justify-center gap-3 animate-[fade-in_0.45s_ease-out_both]">
              <Button
                size="lg"
                className="home-cta-primary relative overflow-hidden h-12 px-7 rounded-xl text-white shadow-[0_10px_30px_-8px_rgba(16,185,129,0.55)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-10px_rgba(16,185,129,0.7)] bg-[linear-gradient(120deg,#059669_0%,#10b981_45%,#14b8a6_100%)] hover:brightness-110 ring-1 ring-white/20 group"
                onClick={() => {
                  document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <span aria-hidden className="absolute inset-0 bg-[linear-gradient(120deg,transparent,rgba(255,255,255,0.35),transparent)] -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <Sparkles className="mr-1.5 h-4 w-4" />
                <span className="relative text-xl md:text-2xl font-bold">{t("home.getStarted")}</span>
                <ArrowRight className="ml-1 h-4 w-4 relative transition-transform group-hover:translate-x-0.5" />
              </Button>
              <Button asChild size="lg" className="home-cta-secondary relative overflow-hidden h-12 px-7 rounded-xl text-white shadow-[0_10px_30px_-8px_rgba(15,23,42,0.5)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-10px_rgba(2,132,199,0.6)] bg-[linear-gradient(120deg,#0f172a_0%,#1e3a8a_55%,#0369a1_100%)] hover:brightness-110 ring-1 ring-white/15 group">
                <Link to="/services" title={t("home.viewServices")}>
                  <span aria-hidden className="absolute inset-0 bg-[linear-gradient(120deg,transparent,rgba(255,255,255,0.25),transparent)] -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  <LayoutGrid className="mr-1.5 h-4 w-4" />
                  <span className="relative text-xl md:text-2xl font-bold">{t("home.viewServices")}</span>
                </Link>
              </Button>
            </div>

            <div className="home-trust-grid mt-14 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl mx-auto animate-[fade-in_0.5s_ease-out_both]">
              {trustBadges.map((b, i) => (
                <div key={i} className="hover-shine group glass rounded-2xl p-4 flex items-start gap-3 shadow-soft hover:shadow-glow hover:-translate-y-0.5 transition-all">
                  <div className="h-14 w-14 rounded-xl gradient-primary text-primary-foreground flex items-center justify-center shadow-glow shrink-0">
                    <b.icon className="h-7 w-7" strokeWidth={2.2} />
                  </div>
                  <div className="flex flex-col justify-start text-left pt-0.5 min-w-0">
                    <p className="text-base sm:text-lg font-bold leading-snug">{t(b.labelKey)}</p>
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{t(b.subKey)}</p>
                  </div>
                </div>

              ))}
            </div>

            <div className="home-stats-grid mt-6 glass rounded-2xl p-4 md:p-5 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto shadow-soft animate-[fade-in_0.55s_ease-out_both]">
              {[
                { icon: Activity, labelKey: "home.stats.activeCases", valueKey: "home.stats.activeCasesValue" },
                { icon: Clock, labelKey: "home.stats.avgFiling", valueKey: "home.stats.avgFilingValue" },
                { icon: LayoutGrid, labelKey: "home.stats.serviceTypes", valueKey: "home.stats.serviceTypesValue" },
                { icon: BadgeCheck, labelKey: "home.stats.compliance", valueKey: "home.stats.complianceValue" },
              ].map((s, i) => (
                <div key={i} className="rounded-xl p-3 text-left min-w-0">
                  <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-muted-foreground whitespace-nowrap">
                    <span className="relative inline-flex h-4 w-4 items-center justify-center shrink-0">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500/40 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.9)]" />
                    </span>
                    <s.icon className="h-4 w-4 text-primary shrink-0" />
                    <span className="truncate">{t(s.labelKey)}</span>
                  </div>
                  <p className="mt-1.5 font-display font-bold text-lg md:text-xl">{t(s.valueKey)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ONGOING CAMPAIGN DEADLINE — SSR-gated to prevent CLS */}
      <CampaignDeadlineSlot />





      {/* SERVICES */}
      <MobileLazyOnVisible minHeight={1100}>
      <section id="services" className="py-12 md:py-16">
        <div className="container-page">
          <SectionHeading
            eyebrow={t("home.servicesSection.eyebrow")}
            title={<>{t("home.servicesSection.titleA")}<span className="gradient-text">{t("home.servicesSection.titleB")}</span></>}
            description={t("home.servicesSection.desc")}
          />
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5 min-h-[820px] sm:min-h-[680px] lg:min-h-[700px]">
            {services.slice(0, 9).map((s, i) => (
              <M key={s.id} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                className="group relative h-full rounded-2xl p-3 sm:p-6 hover:-translate-y-1 transition-all flex flex-col bg-[#0f5132] border border-[#0f5132] shadow-[0_10px_30px_-12px_rgba(15,81,50,0.55)] hover:shadow-[0_20px_45px_-15px_rgba(15,81,50,0.75)]">
                <div className="flex flex-col flex-1">
                  <Link to="/packages" search={{ service: s.slug } as any} title={s.title} className="block">
                    <div className="h-8 w-8 sm:h-12 sm:w-12 rounded-full bg-[#0a3d24] flex items-center justify-center text-emerald-300 ring-1 ring-emerald-400/30 group-hover:scale-105 transition-transform">
                      <Icon name={resolveServiceIcon(s)} className="h-4 w-4 sm:h-6 sm:w-6" />
                    </div>
                    {(() => { const isBn = (i18n.resolvedLanguage || i18n.language || "en").startsWith("bn"); const title = isBn && s.title_bn ? s.title_bn : s.title; const desc = isBn && s.description_bn ? s.description_bn : s.description; return (<>
                      <h3 className="mt-2 sm:mt-4 font-display font-extrabold text-sm sm:text-lg text-white">{title}</h3>
                      <p className="mt-1 sm:mt-1.5 text-xs sm:text-sm text-emerald-50 line-clamp-2">{desc}</p>
                    </>); })()}
                  </Link>
                  <div className="mt-auto pt-2 sm:pt-4 border-t border-emerald-400/25 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-1.5 sm:gap-2">
                    <Link to="/upload" search={{ service: s.slug } as any} title={t("home.servicesSection.uploadDocs", { defaultValue: "Upload Documents" })}
                      className="inline-flex items-center justify-center rounded-md bg-emerald-400 text-emerald-950 px-2 sm:px-3 py-1 text-xs font-bold hover:bg-emerald-300 transition-colors">
                      <Upload className="h-3 w-3 mr-1" />
                      {t("home.servicesSection.uploadDocs", { defaultValue: "Upload Documents" })}
                    </Link>
                    <Link to="/packages" search={{ service: s.slug } as any} title={t("home.servicesSection.learnMore")}
                      className="inline-flex items-center justify-center rounded-md bg-white text-[#0f5132] px-2 sm:px-3 py-1 text-xs font-bold hover:bg-emerald-50 transition-colors">
                      <CreditCard className="h-3 w-3 mr-1" />
                      {t("home.servicesSection.learnMore")}
                    </Link>
                  </div>
                </div>
              </M>
            ))}
          </div>
        </div>
      </section>
      </MobileLazyOnVisible>




      {/* HOW IT WORKS */}
      <MobileLazyOnVisible minHeight={700}>
      <section className="py-12 md:py-16">

        <div className="container-page">
          <SectionHeading
            eyebrow={t("home.howSection.eyebrow")}
            title={<>{t("home.howSection.titleA")}<span className="gradient-text">{t("home.howSection.titleB")}</span></>}
            description={t("home.howSection.desc")}
          />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {howStepKeys.map((s, i) => (
              <M key={s.step} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className={`hover-shine group relative glass rounded-2xl p-5 shadow-card ring-1 ${s.ring} overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-glow`}>
                <div aria-hidden className={`absolute inset-0 bg-gradient-to-br ${s.accent} opacity-80 pointer-events-none`} />
                <span aria-hidden className="pointer-events-none absolute top-2 right-4 z-0 font-display font-extrabold text-5xl tracking-wider text-emerald-500/15 dark:text-white/10 select-none">{s.step}</span>
                <div className="relative z-10 flex items-start justify-end">
                  <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${s.gradient} text-white flex items-center justify-center shadow-lg ring-1 ring-white/30 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-3`}>
                    <s.Icon className="h-7 w-7" strokeWidth={2.2} />
                  </div>
                </div>
                <h3 className="relative mt-3 font-display font-bold text-lg">{t(s.titleKey)}</h3>
                <p className="relative mt-2 text-sm text-muted-foreground">{t(s.descKey)}</p>
              </M>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Button asChild variant="outline" size="lg" className="glass-strong h-12 px-7">
              <Link to="/how-it-works" title={t("home.howSection.seeAll")}>{t("home.howSection.seeAll")} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
      </MobileLazyOnVisible>

      {/* PACKAGES */}
      <MobileLazyOnVisible minHeight={700}>
      <section className="py-12 md:py-16">

        <div className="container-page">
          <SectionHeading
            eyebrow={t("home.packagesSection.eyebrow")}
            title={<>{t("home.packagesSection.titleA")}<span className="gradient-text">{t("home.packagesSection.titleB")}</span></>}
            description={t("home.packagesSection.desc")}
          />
          <div className="mt-12 max-w-2xl mx-auto">
            <M initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Link
                to="/packages"
                title="সব প্যাকেজ ও মূল্য দেখুন"
                activeProps={{ className: "ring-2 ring-emerald-500 shadow-glow -translate-y-1" }}
                className="hover-shine group relative block glass rounded-2xl p-8 sm:p-10 shadow-card cursor-pointer transition-all hover:shadow-glow hover:-translate-y-1 hover:ring-2 hover:ring-emerald-400/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 text-center"
              >
                <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 grid place-items-center shadow-lg transition-transform group-hover:scale-110">
                  <PackageCheck className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-display font-bold text-2xl sm:text-3xl">
                  <span lang="bn" className="font-bn-readable">দাম, ফিচার ও ডেলিভারি</span> <span className="gradient-text">পাশাপাশি তুলনা করুন</span>
                </h3>
                <p lang="bn" className="font-bn-readable mt-3 text-sm sm:text-base text-muted-foreground">
                  Individual Return, e-TIN, BIN/VAT Registration, Trade License, Company Formation — প্রতিটি সেবার ফি, প্রসেসিং টাইম ও অন্তর্ভুক্ত সুবিধা এক নজরে দেখে নিন এবং আপনার বাজেট অনুযায়ী সবচেয়ে উপযোগী প্ল্যানটি বেছে নিন।
                </p>
                <span className="mt-6 inline-flex items-center gap-2 popular-btn h-11 px-6 rounded-md font-medium transition-transform group-hover:translate-x-1">
                  <span lang="bn" className="font-bn-readable">সব প্যাকেজ ও মূল্য দেখুন</span> →
                </span>
              </Link>
            </M>
          </div>


        </div>
      </section>
      </MobileLazyOnVisible>


      {/* TESTIMONIALS — only render (and reserve height) when data exists to avoid an empty vertical gap */}
      {testimonials.length > 0 && (
        <section className="py-12 md:py-16" style={{ minHeight: 720, contentVisibility: "auto", containIntrinsicSize: "1px 720px" }}>
          <div className="container-page">
            <SectionHeading eyebrow={t("home.testimonials.eyebrow")} title={<>{t("home.testimonials.titleA")}<span className="gradient-text">{t("home.testimonials.titleB")}</span></>} description={t("home.testimonials.desc")} />
            <div className="mt-12 grid md:grid-cols-3 gap-4">
              {testimonials.map((t, i) => (
                <M key={t.id} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i*0.05 }}
                  className="hover-shine glass rounded-2xl p-6 shadow-card hover:shadow-glow hover:-translate-y-0.5 transition-all">
                  <div className="flex gap-1 text-amber-500">
                    {Array.from({length: t.rating ?? 5}).map((_,k) => <Star key={k} className="h-4 w-4 fill-current" />)}
                  </div>
                  <p className="mt-3 text-sm leading-relaxed">"{t.content}"</p>
                  <div className="mt-4 pt-4 border-t">
                    <p className="font-semibold text-sm">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}{t.company ? ` · ${t.company}` : ""}</p>
                  </div>
                </M>
              ))}
            </div>
          </div>
        </section>
      )}




      {/* SERVICES CTA (replaces Knowledge Hub on home) */}
      <MobileLazyOnVisible minHeight={600}>
      <section className="py-12 md:py-16">

        <div className="container-page">
          <div className="relative overflow-hidden rounded-3xl glass-strong p-10 md:p-14 shadow-card">
            <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-emerald-400/20 blur-3xl" />
            <div className="relative grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary border border-primary/20 px-3 py-1 text-xs font-semibold">
                  <Sparkles className="h-3.5 w-3.5" /> {t("home.core.eyebrow")}
                </span>
                <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl leading-tight">
                  {t("home.core.titleA")}<span className="gradient-text">{t("home.core.titleB")}</span>{t("home.core.titleC")}
                </h2>
                <p className="mt-4 text-muted-foreground leading-relaxed">
                  {t("home.core.desc")}
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Button asChild size="lg" className="relative overflow-hidden h-12 px-7 rounded-xl bg-[linear-gradient(120deg,#059669_0%,#10b981_45%,#14b8a6_100%)] text-white shadow-[0_10px_30px_-8px_rgba(16,185,129,0.55)] hover:shadow-[0_18px_40px_-10px_rgba(16,185,129,0.75)] hover:brightness-110 ring-1 ring-white/20 transition-all group">
                    <Link to="/services" title={t("home.core.explore")}>
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white/20 ring-1 ring-white/30 mr-1 group-hover:scale-110 group-hover:-rotate-3 transition-transform">
                        <Briefcase className="h-4 w-4" strokeWidth={2.4} />
                      </span>
                      {t("home.core.explore")}
                      <ArrowRight className="ml-1 h-4 w-4" />
                      <span className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-12 translate-x-0 group-hover:translate-x-[400%] transition-transform duration-700" />
                    </Link>
                  </Button>
                  <Button asChild size="lg" className="relative overflow-hidden h-12 px-7 rounded-xl bg-[linear-gradient(120deg,#0f172a_0%,#1e3a8a_55%,#0369a1_100%)] text-white shadow-[0_10px_30px_-8px_rgba(15,23,42,0.5)] hover:shadow-[0_18px_40px_-10px_rgba(2,132,199,0.65)] hover:brightness-110 ring-1 ring-white/15 transition-all group">
                    <Link to="/knowledge-hub" title={t("home.core.knowledgeHub")}>
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white/15 ring-1 ring-white/25 mr-1 group-hover:scale-110 group-hover:rotate-3 transition-transform">
                        <Plane className="h-4 w-4" strokeWidth={2.4} />
                      </span>
                      {t("home.core.knowledgeHub")}
                      <span className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12 translate-x-0 group-hover:translate-x-[400%] transition-transform duration-700" />
                    </Link>
                  </Button>
                </div>
              </div>

              <ul className="grid sm:grid-cols-2 gap-3">
                {[
                  { icon: FileText, labelKey: "home.core.items.itr", to: "/services", title: "আমাদের ট্যাক্স ও ভ্যাট সার্ভিসসমূহ দেখুন", grad: "from-emerald-500 to-teal-500", ring: "ring-emerald-300/40", tint: "from-emerald-500/10 to-teal-500/10" },
                  { icon: BadgeCheck, labelKey: "home.core.items.tin", to: "/services", title: "আমাদের ট্যাক্স ও ভ্যাট সার্ভিসসমূহ দেখুন", grad: "from-sky-500 to-blue-600", ring: "ring-sky-300/40", tint: "from-sky-500/10 to-blue-500/10" },
                  { icon: ShieldCheck, labelKey: "home.core.items.vat", to: "/services", title: "আমাদের ট্যাক্স ও ভ্যাট সার্ভিসসমূহ দেখুন", grad: "from-violet-500 to-fuchsia-500", ring: "ring-fuchsia-300/40", tint: "from-violet-500/10 to-fuchsia-500/10" },
                  { icon: Briefcase, labelKey: "home.core.items.company", to: "/services", title: "আমাদের ট্যাক্স ও ভ্যাট সার্ভিসসমূহ দেখুন", grad: "from-amber-500 to-orange-500", ring: "ring-amber-300/40", tint: "from-amber-500/10 to-orange-500/10" },
                  { icon: Plane, labelKey: "home.core.items.visa", to: "/visa-tax", title: "ভিসা ট্যাক্স সংক্রান্ত তথ্যাদি", grad: "from-rose-500 to-pink-600", ring: "ring-rose-300/40", tint: "from-rose-500/10 to-pink-500/10" },
                  { icon: HeadphonesIcon, labelKey: "home.core.items.expert", to: "/contact", title: "অনলাইন ট্যাক্স ভ্যাট টিমের সাথে যোগাযোগ করুন", grad: "from-cyan-500 to-indigo-500", ring: "ring-cyan-300/40", tint: "from-cyan-500/10 to-indigo-500/10" },
                ].map((s, i) => (
                  <Mli
                    key={s.labelKey}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Link to={s.to} title={s.title} className={`group relative overflow-hidden flex items-center gap-3 rounded-2xl bg-gradient-to-br ${s.tint} ring-1 ${s.ring} backdrop-blur px-4 py-3 hover:-translate-y-0.5 hover:shadow-glow hover:brightness-110 transition-all`}>
                      <span className={`relative z-10 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${s.grad} text-white shadow-lg ring-1 ring-white/30 shrink-0 group-hover:scale-110 group-hover:-rotate-3 transition-transform`}>
                        <s.icon className="h-5 w-5" strokeWidth={2.2} />
                      </span>
                      <span className="relative z-10 text-sm font-semibold flex-1">{t(s.labelKey)}</span>
                      <ArrowRight className="relative z-10 h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition" />
                      <span className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/50 to-transparent skew-x-12 translate-x-0 group-hover:translate-x-[400%] transition-transform duration-700" />
                    </Link>
                  </Mli>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
      </MobileLazyOnVisible>


      {/* CTA */}
      <MobileLazyOnVisible minHeight={500}>
      <section className="py-12 md:py-16">



        <div className="container-page">
          <div className="relative overflow-hidden rounded-3xl p-10 md:p-14 text-primary-foreground shadow-glow">
            <picture>
              {Object.entries(ctaBg.sources).map(([type, srcset]) => (
                <source key={type} type={type} srcSet={srcset} sizes="100vw" />
              ))}
              <img
                src={ctaBg.img.src}
                alt="VAT Return filing service Bangladesh"
                title="VAT Return filing service Bangladesh"
                loading="lazy"
                decoding="async"
                fetchPriority="low"
                width={ctaBg.img.w}
                height={ctaBg.img.h}
                sizes="100vw"
                className="absolute inset-0 h-full w-full object-cover pointer-events-none"
              />
            </picture>
            <div className="absolute inset-0 bg-gradient-to-r from-primary/55 via-primary/30 to-transparent pointer-events-none" />
            <div className="absolute inset-0 bg-black/20 pointer-events-none" />
            <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-white/15 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="relative max-w-2xl">
              <h2 className="font-display font-bold text-white text-4xl md:text-5xl lg:text-6xl leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]">{t("home.cta.title")}</h2>
              <p className="mt-4 text-lg md:text-xl text-white/95 drop-shadow-[0_1px_4px_rgba(0,0,0,0.4)]">{t("home.cta.desc")}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/95 h-12 px-7">
                  <Link to="/upload" title={t("home.cta.upload")}><Upload className="mr-2 h-4 w-4" /> {t("home.cta.upload")}</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/40 bg-white/10 text-primary-foreground hover:bg-white/20 h-12 px-7">
                  <Link to="/contact" title={t("home.cta.expert")}>{t("home.cta.expert")}</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
      </MobileLazyOnVisible>



      <MobileLazyOnVisible>
        <Suspense fallback={null}>
          <TestimonialsSection />
        </Suspense>
      </MobileLazyOnVisible>


      {/* SEO: organic keyword paragraph for income tax + VAT (moved to bottom) */}
      <MobileLazyOnVisible>
        <section className="py-10 md:py-14">
          <div className="container-page max-w-4xl">
            {(() => {
              const isBn = (i18n.resolvedLanguage || i18n.language || "en").startsWith("bn");
              const s = siteSettings || {};
              const pick = (bn: string, en: string, fallback: string) => {
                const val = isBn ? (s[bn] || s[en]) : s[en];
                return (val && String(val).trim()) || fallback;
              };
              const heading = pick(
                "home_seo1_heading_bn",
                "home_seo1_heading_en",
                "Income Tax & VAT in Bangladesh — handled online",
              );
              const bodyDefault = `We make <strong>income tax return filing bangladesh</strong> simple — from <strong>e-TIN registration bangladesh</strong> and tax computation to <strong>online tax payment BD</strong> via e-payment NBR. Whether you need an <strong>individual income tax filing bangladesh</strong>, salary submission or <strong>corporate tax filing</strong>, our NBR-experienced team prepares, reviews and submits everything digitally with the correct tax zone, rebate and exemption claims.\n\nOn the VAT side we cover <strong>vat registration BD</strong>, BIN certificate, <strong>Mushak 9.1 BD</strong> monthly submissions, Mushak 6.3, supplementary duty, turnover tax and NBR-approved VAT software setup — a complete <strong>vat bangladesh</strong> compliance partner for SMEs, e-commerce sellers, importers and corporates.`;

              if (isBn && !(s["home_seo1_body_bn"] && String(s["home_seo1_body_bn"]).trim())) {
                const bnHeading = (s["home_seo1_heading_bn"] && String(s["home_seo1_heading_bn"]).trim())
                  || "বাংলাদেশে আয়কর এবং ভ্যাট সেবা — এখন সম্পূর্ণ অনলাইন";
                return (
                  <>
                    <h2 className="font-display text-3xl font-bold mb-4">{bnHeading}</h2>
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-4">
                      আমরা বাংলাদেশে আয়কর রিটার্ন দাখিল করার পুরো প্রক্রিয়াটিকে করেছি একদম সহজ ও ঝামেলাহীন। ই-টিন (e-TIN) রেজিস্ট্রেশন, নিখুঁত কর গণনা থেকে শুরু করে এনবিআর (NBR) ই-পেমেন্টের মাধ্যমে অনলাইনে ট্যাক্স জমা দেওয়া—সবকিছুই এখন এক জায়গায়। আপনার ব্যক্তিগত আয়কর রিটার্ন, সেলারি সাবমিশন কিংবা কর্পোরেট ট্যাক্স ফাইলিং যাই প্রয়োজন হোক না কেন, আমাদের এনবিআর-অভিজ্ঞ টিম সঠিক ট্যাক্স জোন, রেয়াত (Rebate) এবং ট্যাক্স এক্সেম্পশন ক্লেইম নিশ্চিত করে সম্পূর্ণ ডিজিটাল উপায়ে সবকিছু প্রস্তুত, নিখুঁতভাবে পর্যালোচনা এবং দাখিল করে থাকে।
                    </p>
                    <h3 className="text-xl font-semibold mt-4 mb-2">আমাদের ভ্যাট (VAT) কমপ্লায়েন্স সেবাসমূহ:</h3>
                    <ul className="list-disc pl-5 space-y-2 text-sm md:text-base text-muted-foreground leading-relaxed mb-4">
                      <li><strong>ভ্যাট রেজিস্ট্রেশন (VAT Registration BD)</strong> এবং বিআইএন (BIN) সার্টিফিকেট সংগ্রহ</li>
                      <li>প্রতি মাসের <strong>মূসক ৯.১ (Mushak 9.1)</strong> অনলাইন রিটার্ন সাবমিশন</li>
                      <li><strong>মূসক ৬.৩ (Mushak 6.3)</strong> বা চালানপত্র ব্যবস্থাপনা</li>
                      <li>সম্পূরক শুল্ক (Supplementary Duty) এবং টার্নওভার ট্যাক্স হিসাবকরণ</li>
                      <li>এনবিআর-অনুমোদিত (NBR-approved) <strong>ভ্যাট সফটওয়্যার সেটআপ</strong></li>
                    </ul>
                    <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
                      আমরা ক্ষুদ্র ও মাঝারি শিল্প (SMEs), ই-কমার্স ব্যবসায়ী, আমদানিকারক এবং কর্পোরেট কোম্পানিগুলোর জন্য বাংলাদেশে একটি সম্পূর্ণ এবং নির্ভরযোগ্য ভ্যাট কমপ্লায়েন্স পার্টনার হিসেবে কাজ করি।
                    </p>
                  </>
                );
              }

              const body = pick("home_seo1_body_bn", "home_seo1_body_en", bodyDefault);
              const paras: string[] = body.split(/\n\s*\n/).map((p: string) => p.trim()).filter(Boolean);
              return (
                <>
                  <h2 className="font-display text-2xl md:text-3xl font-extrabold mb-4">{heading}</h2>
                  {paras.map((p: string, i: number) => (
                    <p
                      key={i}
                      className={`text-sm md:text-base text-muted-foreground leading-relaxed ${i < paras.length - 1 ? "mb-3" : ""}`}
                      dangerouslySetInnerHTML={{ __html: p }}
                    />
                  ))}
                </>
              );
            })()}
          </div>
        </section>
      </MobileLazyOnVisible>

      {/* "Why Choose" content moved to dedicated /why-choose-us route */}



      {/* ONLINE VAT SERVICES */}
      <MobileLazyOnVisible minHeight={900}>

      <section id="online-vat" className="py-12 md:py-16 bg-primary/5">
        <div className="container-page">
          <SectionHeading
            eyebrow="Online VAT"
            title={<>Online <span className="gradient-text">VAT Services</span> in Bangladesh</>}
            description="File your VAT digitally in Bangladesh — registration, BIN, monthly Mushak 9.1 submission and expert consultancy, fully NBR-compliant."
          />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: BadgeCheck, title: "VAT Registration", desc: "Apply digitally and get your BIN (Business Identification Number) — no office visits required.", grad: "from-emerald-500 to-teal-500" },
              { icon: FileText, title: "Monthly VAT Filing (Mushak 9.1)", desc: "Monthly VAT preparation and submission to NBR with verified documents and reconciliation.", grad: "from-sky-500 to-blue-600" },
              { icon: ShieldCheck, title: "VAT Compliance & Audit", desc: "Stay 100% NBR-compliant with our digital VAT compliance checks, input-output review and audit support.", grad: "from-violet-500 to-fuchsia-500" },
              { icon: Activity, title: "VAT Consultancy", desc: "Expert VAT advisory for SMEs, e-commerce, importers and service providers across Bangladesh.", grad: "from-amber-500 to-orange-500" },
              { icon: Clock, title: "Fast Turnaround", desc: "Most VAT services completed in 1–3 working days with real-time status updates on your dashboard.", grad: "from-rose-500 to-pink-600" },
              { icon: HeadphonesIcon, title: "Dedicated VAT Support", desc: "Talk to a certified VAT consultant on WhatsApp, chat or call — your trusted partner, all year round.", grad: "from-cyan-500 to-indigo-500" },
            ].map((item, i) => (
              <M
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="hover-shine group rounded-xl border border-primary/15 bg-card p-5 hover:border-primary/40 hover:shadow-glow hover:-translate-y-1 transition-all"
              >
                <div className={`inline-flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br ${item.grad} text-white shadow-lg ring-1 ring-white/30 group-hover:scale-110 group-hover:-rotate-3 transition-transform`}>
                  <item.icon className="h-7 w-7" strokeWidth={2.2} />
                </div>
                <h3 className="mt-4 font-semibold text-lg">{item.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </M>
            ))}
          </div>

          <div className="relative mt-10 overflow-hidden rounded-2xl border border-primary/20 bg-card p-6 md:p-8">
            {/* Decorative VAT-themed background icons */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden text-primary/[0.07] dark:text-primary/[0.10]">
              <Receipt className="absolute -top-4 -left-4 h-32 w-32 -rotate-12" />
              <FileText className="absolute top-6 right-8 h-20 w-20 rotate-6" />
              <Calculator className="absolute bottom-4 left-10 h-24 w-24 rotate-3" />
              <Landmark className="absolute -bottom-6 -right-6 h-36 w-36 -rotate-6" />
              <BadgePercent className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-40 w-40 rotate-12" />
              <Banknote className="absolute top-1/3 right-1/3 h-16 w-16 -rotate-12" />
              <ShieldCheck className="absolute bottom-1/3 left-1/4 h-16 w-16 rotate-6" />
            </div>
            <div className="relative">
              <h3 className="text-xl md:text-2xl font-semibold">Why choose our VAT services?</h3>
              <ul className="mt-5 grid sm:grid-cols-2 gap-3 text-sm">
                {[
                  { text: "100% digital VAT registration and monthly filing", Icon: Laptop, bg: "bg-purple-600" },
                  { text: "NBR-certified consultants for full VAT compliance", Icon: ShieldCheck, bg: "bg-sky-500" },
                  { text: "Affordable pricing — packages from ৳1,500", Icon: Coins, bg: "bg-amber-500" },
                  { text: "Secure document upload with end-to-end privacy", Icon: Lock, bg: "bg-rose-500" },
                  { text: "Real-time tracking of every VAT submission", Icon: Activity, bg: "bg-emerald-500" },
                  { text: "Bangla & English support — call, chat or WhatsApp", Icon: MessageSquare, bg: "bg-indigo-500" },
                ].map(({ text, Icon, bg }) => (
                  <li key={text} className="flex items-start gap-3">
                    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${bg} text-white shadow-sm`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="pt-0.5 font-medium text-foreground">{text}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild className="bg-amber-500 hover:bg-amber-600 text-black font-semibold shadow-md">
                  <Link to="/services" title="Start VAT Filing">Start VAT Filing <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
                <Button asChild variant="outline" className="border-2 border-white text-white bg-white/5 hover:bg-white/10 hover:text-white">
                  <Link to="/contact" title="Talk to a VAT Expert">Talk to a VAT Expert</Link>
                </Button>
              </div>

            </div>
          </div>

        </div>
      </section>
      </MobileLazyOnVisible>

      <section className="container-page pb-8">
        <ServiceDetailSection content={SERVICE_DETAIL_CONTENT["home"]} />
      </section>




      <DeferUntilIdle>
        <Suspense fallback={null}>
          <TaxCalculatorWidget />
          <PaymentLinksWidget />
          <VisaTaxFloatingButton />
          <PopupOffer />
          <FloatingPromoCard />
        </Suspense>
      </DeferUntilIdle>
      
    </SiteShell>
  );
}
